import { useState } from "react";
import { Search, X, Settings, Eye } from "lucide-react";
import UserManagementModal from "./UserManagementModal";
import DeleteConfirmModal from "./DeleteConfirmModal";

const UsersTab = ({
  regularUsers,
  trades,
  withdrawals = [],
  deposits = [],
  token,
  onDeleteUser,
  onUpdate
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);

  // Filter users by search query (including client_id)
  const filteredUsers = regularUsers.filter(user => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      user.email?.toLowerCase().includes(query) ||
      user.name?.toLowerCase().includes(query) ||
      user.phone?.toLowerCase().includes(query) ||
      user.client_id?.includes(query)
    );
  });

  // Get user stats
  const getUserStats = (user) => {
    const userTrades = trades.filter(t => t.user_id === user.id);
    const activeTrades = userTrades.filter(t => t.status === "active");
    const userWithdrawals = withdrawals.filter(w => w.user_id === user.id);
    const pendingWithdrawals = userWithdrawals.filter(w => w.status === "pending" || w.status === "in_progress");
    
    return {
      totalTrades: userTrades.length,
      activeTrades: activeTrades.length,
      pendingWithdrawals: pendingWithdrawals.length
    };
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="font-chivo font-bold text-xl text-white">Пользователи</h2>
          <p className="text-[#A1A1AA] text-sm mt-1">Нажмите на пользователя для управления</p>
        </div>
        <div className="text-[#A1A1AA] text-sm">
          Всего: <span className="text-white font-medium">{regularUsers.length}</span>
        </div>
      </div>
      
      <div className="bg-[#0A0A0A] rounded-lg border border-[#1F1F1F] overflow-hidden">
        {/* Search bar */}
        <div className="p-4 border-b border-[#1F1F1F]">
          <div className="relative max-w-md">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Поиск по ID, имени, email или телефону..."
              className="w-full bg-[#000000] border border-[#1F1F1F] rounded pl-10 pr-10 py-2.5 text-white placeholder-[#A1A1AA] focus:border-white focus:outline-none"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A1A1AA] hover:text-white transition-colors"
              >
                <X size={18} />
              </button>
            )}
          </div>
          {searchQuery && (
            <div className="mt-2 text-sm text-[#A1A1AA]">
              Найдено: {filteredUsers.length} из {regularUsers.length}
            </div>
          )}
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="admin-users-table">
            <thead>
              <tr className="border-b border-[#1F1F1F]">
                <th className="text-left text-[#A1A1AA] text-sm font-normal py-4 px-4">ID</th>
                <th className="text-left text-[#A1A1AA] text-sm font-normal py-4 px-4">Пользователь</th>
                <th className="text-left text-[#A1A1AA] text-sm font-normal py-4 px-4">Телефон</th>
                <th className="text-left text-[#A1A1AA] text-sm font-normal py-4 px-4">Баланс</th>
                <th className="text-left text-[#A1A1AA] text-sm font-normal py-4 px-4">В торговле</th>
                <th className="text-left text-[#A1A1AA] text-sm font-normal py-4 px-4">Сделки</th>
                <th className="text-left text-[#A1A1AA] text-sm font-normal py-4 px-4">Статус</th>
                <th className="text-left text-[#A1A1AA] text-sm font-normal py-4 px-4"></th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-12 text-[#A1A1AA]">
                    {searchQuery ? "Пользователи не найдены" : "Нет пользователей"}
                  </td>
                </tr>
              ) : (
                filteredUsers.map((u) => {
                  const stats = getUserStats(u);
                  
                  return (
                    <tr 
                      key={u.id} 
                      className="border-b border-[#1F1F1F]/50 hover:bg-[#1A1A1A]/50 cursor-pointer transition-colors"
                      onClick={() => setSelectedUser(u)}
                    >
                      <td className="py-4 px-4">
                        <span className="text-[#EDEDED] font-mono">{u.client_id || "-"}</span>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-[#EDEDED] font-bold">
                            {u.name?.charAt(0)?.toUpperCase() || "U"}
                          </div>
                          <div>
                            <div className="text-white font-medium">{u.name || "Без имени"}</div>
                            <div className="text-[#A1A1AA] text-sm">{u.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <span className="text-[#A1A1AA]">{u.phone || "-"}</span>
                      </td>
                      <td className="py-4 px-4">
                        <span className="font-mono text-[#34D399]">${(u.balance || 0).toFixed(2)}</span>
                      </td>
                      <td className="py-4 px-4">
                        <span className="font-mono text-[#EDEDED]">${(u.trading_balance || 0).toFixed(2)}</span>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-2">
                          <span className="text-white">{stats.totalTrades}</span>
                          {stats.activeTrades > 0 && (
                            <span className="bg-white/15 text-[#EDEDED] px-2 py-0.5 rounded text-xs">
                              {stats.activeTrades} активных
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <span className={`px-2 py-1 rounded text-xs ${
                          u.is_approved 
                            ? "bg-[#34D399]/15 text-[#34D399]" 
                            : "bg-[#F87171]/15 text-[#F87171]"
                        }`}>
                          {u.is_approved ? "Активен" : "Заблокирован"}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedUser(u);
                          }}
                          className="flex items-center gap-1.5 bg-white/15 text-[#EDEDED] px-3 py-1.5 rounded text-sm hover:bg-[#1A1A1A]/25 transition-colors"
                        >
                          <Settings size={14} />
                          Управление
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* User Management Modal */}
      {selectedUser && (
        <UserManagementModal
          user={selectedUser}
          trades={trades}
          withdrawals={withdrawals}
          deposits={deposits}
          token={token}
          onClose={() => setSelectedUser(null)}
          onUpdate={() => {
            onUpdate();
            // Refresh user data
            const updatedUser = regularUsers.find(u => u.id === selectedUser.id);
            if (updatedUser) setSelectedUser(updatedUser);
          }}
        />
      )}
      
      {/* Delete Confirmation Modal */}
      {deleteModal && (
        <DeleteConfirmModal
          isOpen={!!deleteModal}
          onClose={() => setDeleteModal(null)}
          onConfirm={() => onDeleteUser(deleteModal.id)}
          title="Удаление пользователя"
          itemName={deleteModal.email}
          itemType="пользователя"
          warningText="Все данные пользователя будут удалены: сделки, уведомления, сообщения, заявки на вывод."
          confirmText="УДАЛИТЬ"
        />
      )}
    </div>
  );
};

export default UsersTab;
